import type { GridLocaleText, GridRowId } from "@mui/x-data-grid";
import { useCallback, useEffect, useMemo, useState } from "react";

export type GridRow<IdKey extends string> = Record<IdKey, GridRowId>;

export enum SelectionMode {
  Normal = "include",
  ExclusionBased = "exclude",
}

// heads up: MUI DataGrid v8 uses Sets natively - https://mui.com/x/migration/migration-data-grid-v7/#selection
// the version we use as of writing (v7) uses arrays instead
// this hook uses Sets internally but exposes computed arrays to satisfy v7 API

const createIdSet = <T extends GridRowId>(): Set<T> => new Set();

export function useRowSelection<IdKey extends string, T extends GridRow<IdKey>>(
  displayedItems: T[] | undefined,
  idKey: IdKey,
  totalItemCount: number | undefined,
) {
  const [selectedIds, setSelectedIds] = useState<Set<T[IdKey]>>(createIdSet);
  const [excludedIds, setExcludedIds] = useState<Set<T[IdKey]>>(createIdSet);
  const [selectionMode, setSelectionMode] = useState<SelectionMode>(SelectionMode.Normal);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setExcludedIds(new Set());
    setSelectionMode(SelectionMode.Normal);
  }, []);

  /*
    MUI DataGrid's typing expects the row selection callback to accept row IDs that are either strings or numbers.
    However, our own typing ensures that the type of row IDs are always an expected narrowed type (`T[IdKey]`).
    So, we type the function parameter to be the general `GridRowId` that MUI expects, but then we cast it internally to the right type.
   */
  const handleRowSelection = useCallback(
    (newSelectedIdsUntyped: readonly GridRowId[]) => {
      const newSelectedIds = newSelectedIdsUntyped as T[IdKey][];
      const newSelectionDifference = newSelectedIds.length - selectedIds.size;
      const isSelectAllOrDeselectAll = Math.abs(newSelectionDifference) > 1;

      if (isSelectAllOrDeselectAll) {
        const isSelectAll = newSelectedIds.length > selectedIds.size;

        if (isSelectAll) {
          setSelectionMode(SelectionMode.ExclusionBased);
          setSelectedIds(new Set(newSelectedIds));
          setExcludedIds(new Set());
        } else {
          setSelectionMode(SelectionMode.Normal);
          setSelectedIds(new Set());
          setExcludedIds(new Set());
        }
        return;
      }

      setSelectedIds(new Set(newSelectedIds));

      if (!displayedItems) return;

      if (selectionMode === SelectionMode.ExclusionBased) {
        let modifiedId: T[IdKey];

        for (const item of displayedItems) {
          const itemId = item[idKey];
          const isPrevSelected = selectedIds.has(itemId);
          const isNowSelected = newSelectedIds.includes(itemId);
          if (isPrevSelected !== isNowSelected) {
            modifiedId = itemId;
            break;
          }
        }

        const isAddingExclusion = newSelectionDifference < 0;
        const isRemovingExclusion = !isAddingExclusion;

        if (isAddingExclusion) {
          setExcludedIds(prev => new Set(prev).add(modifiedId));
        } else if (isRemovingExclusion) {
          setExcludedIds(prev => {
            const modifiedSet = new Set(prev);
            prev.delete(modifiedId);
            return modifiedSet;
          });
        }
      }
    },
    [displayedItems, idKey, selectedIds, selectionMode],
  );

  useEffect(() => {
    if (!displayedItems) return;

    if (selectionMode === SelectionMode.ExclusionBased) {
      let newSelection: Set<T[IdKey]> | undefined;

      for (const item of displayedItems) {
        const itemId = item[idKey];

        if (!excludedIds.has(itemId) && !selectedIds.has(itemId)) {
          if (!newSelection) newSelection = new Set(selectedIds);
          newSelection.add(itemId);
        }
      }

      if (newSelection) setSelectedIds(newSelection);
    }
  }, [selectionMode, displayedItems, idKey, excludedIds, selectedIds]);

  const selectedIdArray = useMemo(() => Array.from(selectedIds), [selectedIds]);
  const excludedIdArray = useMemo(() => Array.from(excludedIds), [excludedIds]);

  const effectiveSelectionCount = useMemo(() => {
    if (selectionMode === SelectionMode.ExclusionBased) {
      return (totalItemCount ?? 0) - excludedIds.size;
    } else {
      return selectedIds.size;
    }
  }, [selectionMode, totalItemCount, excludedIds.size, selectedIds.size]);

  const localeText: Partial<GridLocaleText> = useMemo(
    () => ({
      footerRowSelected(_count) {
        return effectiveSelectionCount > 1
          ? `${effectiveSelectionCount.toLocaleString()} rows selected`
          : `${effectiveSelectionCount.toLocaleString()} row selected`;
      },
    }),
    [effectiveSelectionCount],
  );

  return {
    selectedIds: selectedIdArray,
    excludedIds: excludedIdArray,
    handleRowSelection,
    localeText,
    selectionMode,
    clearSelection,
    effectiveSelectionCount,
  };
}
